import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, type ApiClient } from "./api";

export interface Loaded<T> {
  value: T;
  /** True until the first answer, so a screen can tell "empty" from "not yet". */
  loading: boolean;
  problem: string | null;
  refresh: () => void;
  /** Replace the value without asking, e.g. with the answer to a write that just returned it. */
  set: (value: T) => void;
}

export interface LoadOptions<T> {
  /**
   * How long to wait before asking again. A number polls for as long as the hook is mounted; a
   * function decides from the latest value, and `null` stops polling until a later answer
   * starts it again. Absent means read once and on `refresh`.
   */
  poll?: number | ((value: T) => number | null);
  /** False holds off reading: no key yet, or a panel nobody has opened. Defaults to true. */
  enabled?: boolean;
}

/**
 * One read and everything a screen needs around it: the value, whether the first answer has
 * arrived, why the last read failed, and an optional poll.
 *
 * A failed read keeps the last value. A stale list, badge or checklist is more useful than an
 * empty one, and one request that did not get through should not blank the screen. A read that
 * succeeds clears the problem a transient failure left behind.
 *
 * Answers land in the order they were asked: when the read changes (a new id) or a refresh
 * overtakes a poll, a slower earlier answer is dropped instead of overwriting a newer one. A poll
 * tick that comes due while a read is still out is skipped, so a slow Server never has requests
 * stacking up against it.
 *
 * `read` must be stable (declared outside the component, or memoised on its key), or every render
 * reads again. `problem` is the message shown when a read fails; a function receives the failure.
 */
export function useLoaded<T>(
  api: ApiClient,
  read: (api: ApiClient) => Promise<T>,
  initial: T,
  problem: string | ((cause: unknown) => string),
  options: LoadOptions<T> = {},
): Loaded<T> {
  const { poll, enabled = true } = options;

  const [value, setValue] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);

  // Read through refs, so a new message function or a value that changed does not tear the
  // reader down and start it again.
  const describe = useRef(problem);
  describe.current = problem;
  const asked = useRef(0);
  const outstanding = useRef(0);

  const load = useCallback(
    async (fromPoll = false) => {
      if (fromPoll && outstanding.current > 0) return;

      const ticket = ++asked.current;
      outstanding.current++;

      try {
        const next = await read(api);
        if (ticket !== asked.current) return;
        setValue(next);
        setFailure(null);
      } catch (cause: unknown) {
        if (ticket !== asked.current) return;
        const message = describe.current;
        setFailure(typeof message === "function" ? message(cause) : message);
      } finally {
        outstanding.current--;
        if (ticket === asked.current) setLoading(false);
      }
    },
    [api, read],
  );

  useEffect(() => {
    if (enabled) void load();
  }, [load, enabled]);

  const interval = poll === undefined ? null : typeof poll === "number" ? poll : poll(value);

  useEffect(() => {
    if (!enabled || interval === null) return;

    const timer = setInterval(() => void load(true), interval);
    return () => clearInterval(timer);
  }, [enabled, interval, load]);

  const refresh = useCallback(() => void load(), [load]);
  const set = useCallback((next: T) => {
    // Anything still out was asked before this value existed.
    asked.current++;
    setValue(next);
    setFailure(null);
    setLoading(false);
  }, []);

  return { value, loading, problem: failure, refresh, set };
}

/** The Server's own explanation when it gave one, `fallback` otherwise. */
export function apiProblem(fallback: string): (cause: unknown) => string {
  return (cause) => (cause instanceof ApiError ? cause.message : fallback);
}
